import { BoatConfigurationRawData, BoatMfgRawData, BoatModelRawData } from '@ahoy/types/boat-data';
import { Option } from '@ahoy/model/dist/question';
import { ApiService } from './api-service';
import { ReactiveList } from '../model/reactive-list';
import { IDataProviderService } from './data-provider';
import { WebSectionTrackingData } from '@ahoy/types';
import { eventBus } from '@APP/utils/event-bus';
import { Events } from '@APP/events/app-events';


let nextYear;
const currentDate = new Date();
nextYear = currentDate.getFullYear() + 1;
const yearsOpts: Option<number, undefined>[] = [];
for (let year = nextYear; year >= 1970; year--) {
  yearsOpts.push({
    label: year.toString(),
    value: year,
  });
};

type BoatData = {
  readonly year: ReactiveList<number, undefined>;
  readonly vendor: ReactiveList<string, BoatMfgRawData>;
  readonly model: ReactiveList<string, BoatModelRawData>;
  readonly configuration: ReactiveList<string, BoatConfigurationRawData>;
};

export class BoatApiService extends ApiService implements IDataProviderService {

  static serviceName = 'boatApiService';

  private vendorFilter: string = '';

  private boatData: BoatData = {
    year: new ReactiveList('boat-data-year', yearsOpts),
    configuration: new ReactiveList('boat-data-configuration'),
    model: new ReactiveList('boat-data-model'),
    vendor: new ReactiveList('boat-data-vendor'),
  };

  private trailerData: BoatData = {
    year: new ReactiveList('trailer-data-year', yearsOpts),
    configuration: new ReactiveList('trailer-data-configuration'),
    model: new ReactiveList('trailer-data-model'),
    vendor: new ReactiveList('trailer-data-vendor'),
  };

  private category: string;
  private data: BoatData = this.boatData;

  constructor(categoryId?: string) {
    super('boatApiEndpoint', 'boat');
    this.category = categoryId ? `&categoryId=${categoryId}` : '';
    this.data = categoryId ? this.trailerData : this.boatData;
    this.data.year.on('selection-changed', () => this.loadVendors());
    this.data.vendor.on('selection-changed', () => this.loadModels());
    this.data.model.on('selection-changed', () => this.loadConfigurations());
    this.data.year.setState('ready');
  }


  getList(name: 'configuration'): ReactiveList<string, BoatConfigurationRawData>;
  getList(name: 'model'): ReactiveList<string, BoatModelRawData>;
  getList(name: 'vendor'): ReactiveList<string, BoatMfgRawData>;
  getList(name: 'year'): ReactiveList<number, undefined>;
  getList(name: keyof BoatData) {
    return this.data[name];
  }

  override async http<T = any>(path: string, data?: any) {
    if (!this.requestOpts.extraHeaders.length && this.getToken()) {
      this.requestOpts.extraHeaders.push(['Authorization', 'Bearer ' + this.getToken()]);
    }
    return super.http<T>(path, data);
  }

  async loadVendors() {
    const { year, vendor } = this.data;
    if (!year.selection) return;
    vendor.setState('pending');
    this.data.configuration.select(undefined);
    this.data.configuration.options = [];
    this.data.model.select(undefined);
    this.data.model.options = [];
    const data = await this.http(`/manufacturers?year=${year.selection.value}${this.category}`);
    vendor.options = data.data.map((data: BoatMfgRawData) => ({
      label: data.manufacturerName,
      value: data.manufacturerName,
      data,
    }));
    if (this.vendorFilter != '') {
      vendor.options = vendor.options.filter((data) => data.label == this.vendorFilter);
    }
    vendor.setState('ready');
  }

  async loadModels() {
    const { model, vendor, year } = this.data;
    if (!vendor.selection || !year.selection || (vendor.selection.isOther && model.selection?.isOther)) {
      return;
    }

    model.setState('pending');
    model.options = [];
    model.select(undefined);
    this.data.configuration.select(undefined);
    this.data.configuration.options = [];

    if (vendor.selection.isOther || !vendor.selection.data?.manufacturerId) {
      model.setState('ready');
      return;
    }

    const data = await this.http(`/models?year=${year.selection.value}&manufacturerId=${vendor.selection.data!.manufacturerId}${this.category}`);
    model.options = data.data.map((data: BoatModelRawData) => ({
      label: data.modelName,
      value: data.modelName,
      data,
    }));
    model.setState('ready');
  }

  private toTransientString(src: string): string {
    return src === 'Yes' ? '$_BOOL_true' : '$_BOOL_false';
  }


  async loadConfigurations() {
    const { model, vendor, year, configuration } = this.data;
    if (!model.selection || !vendor.selection || !year.selection) return;

    configuration.setState('pending');

    if (model.selection.isOther || !model.selection.data?.modelId) {
      this.data.configuration.options = [];
      this.data.configuration.select(undefined);
      configuration.setState('ready');
      return;
    }
    try {
      const data = await this.http(`/configurations?modelId=${model.selection.data?.modelId}&manufacturerId=${vendor.selection.data?.manufacturerId}&year=${year.selection.value}`);
      configuration.options = data.data.map((data: BoatConfigurationRawData) => ({
        label: 'Configuration #' + data.configurationId,
        value: 'Configuration #' + data.configurationId,
        data,
      })).map((opt: any) => ({
        ...opt, ...{
          data: {
            ...opt.data, ...{
              hasTrailer: this.toTransientString(opt.data.hasTrailer),
              dieselFuel: this.toTransientString(opt.data.dieselFuel),
            },
          },
        },
      }));
      configuration.setState('ready');
      configuration.select(undefined);
    } catch (err:any) {
      const errorText = err?.details || 'Error getting the configurations data from Boat API';
      const data: WebSectionTrackingData = {
        section: '',
        next_section: '',
        operation: '',
        errorMessage: errorText,
        sendToSlackOnly: true,
        slackMessage: 'This means that the user is not able to get the configurations data from Boat API and is prompted to enter the boat details manually.',
      };
      eventBus().emit(Events.Type.SaveWebSectionTracking, data);
      this.data.configuration.options = [];
      this.data.configuration.select(undefined);
      configuration.setState('ready');
      return;
    }     
  }

  async getValues(configurationId: string) {
    const data = await this.http(`/values?configurationId=${configurationId}`);
    return data;
  }

  setVendorFilter(vendorName: string) {
    this.vendorFilter = vendorName;
  }

}

BoatApiService.define();